import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { useState } from 'react';

import { BarsIcon, XmarkIcon } from '@/icons/fa/regular';
import Footer from '@/shared/components/footer';

import { NavbarItem } from './navbar';

function MobileSidebar({ open, close, children }: React.PropsWithChildren<{ open: boolean; close: () => void }>) {
  return (
    <Headless.Dialog open={open} onClose={close} className="lg:hidden">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <Headless.DialogPanel
        transition
        className="fixed inset-y-0 w-full max-w-80 p-2 transition duration-300 ease-in-out data-closed:-translate-x-full"
      >
        <div className="flex h-full flex-col rounded-lg bg-white ring-1 shadow-xs ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
          <div className="-mb-3 px-4 pt-3">
            <Headless.CloseButton as={NavbarItem} aria-label="Close navigation">
              <XmarkIcon className="fill-zinc-800" />
            </Headless.CloseButton>
          </div>
          {children}
        </div>
      </Headless.DialogPanel>
    </Headless.Dialog>
  );
}

export function StackedLayout({
  isAuthenticated,
  isImpersonating,
  navbar,
  sidebar,
  children
}: React.PropsWithChildren<{ isAuthenticated: boolean; isImpersonating?: boolean; navbar: React.ReactNode; sidebar: React.ReactNode }>) {
  const [showSidebar, setShowSidebar] = useState(false);

  return (
    <div
      className={clsx(
        'relative isolate flex h-svh w-full flex-col bg-primary-800 dark:bg-zinc-900 dark:lg:bg-zinc-950',
        isImpersonating && 'bg-[url("/img/impersonation_watermark.png")] bg-[length:200px_72px]'
      )}
    >
      {/* Sidebar on mobile */}
      <MobileSidebar open={showSidebar} close={() => setShowSidebar(false)}>
        {sidebar}
      </MobileSidebar>

      {/* Navbar */}
      <header className="flex items-center px-4">
        {isAuthenticated && (
          <div className="mr-2 py-2.5 lg:hidden">
            <NavbarItem onClick={() => setShowSidebar(true)} aria-label="Open navigation">
              <BarsIcon />
            </NavbarItem>
          </div>
        )}
        <div className="min-w-0 flex-1">{navbar}</div>
      </header>

      {/* Content */}
      <main className="flex flex-1 flex-col bg-gray-300">
        <div className="grow bg-gray-300 lg:ring-1 lg:shadow-xs lg:ring-zinc-950/5 dark:lg:bg-zinc-900 dark:lg:ring-white/10 min-h-0">
          <div className="mx-auto max-w-7xl h-full bg-white">
            {children}
          </div>
        </div>
      </main>

      <Footer/>
    </div>
  );
}
